import React, { useState } from 'react';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import { motion } from 'framer-motion';
import WebhookIcon from '@mui/icons-material/Webhook';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LinkIcon from '@mui/icons-material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TuneIcon from '@mui/icons-material/Tune';

const MotionCard = motion.create(Card);

const WebhookCard = ({
  webhook,
  policyName,
  onEdit,
  onDelete,
  index = 0,
}) => {
  const [hovered, setHovered] = useState(false);

  const {
    id,
    name,
    url,
    field_mappings = [],
    headers = [],
    auth_username,
    created_at,
  } = webhook;

  const hasAuth = !!auth_username;
  
  const fieldMappingsCount = Array.isArray(field_mappings) 
    ? field_mappings.length 
    : Object.keys(field_mappings || {}).length;
  
  const headersCount = Array.isArray(headers) 
    ? headers.length 
    : Object.keys(headers || {}).length;

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Truncate URL for display
  const displayUrl = url?.length > 35 ? `${url.substring(0, 35)}...` : url;

  return (
    <MotionCard
      variant="outlined"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.3, 
        delay: index * 0.05,
        ease: 'easeOut',
      }}
      whileHover={{ 
        y: -4, 
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        transition: { duration: 0.2 },
      }}
      sx={{
        p: 2.5,
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minHeight: 180,
        transition: 'border-color 0.2s',
        '&:hover': {
          borderColor: 'primary.400',
        },
      }}
      onClick={() => onEdit(webhook)}
    >
      {/* Action buttons - visible on hover */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 0.5,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s',
        }}
      >
        <IconButton
          size="sm"
          variant="soft"
          color="neutral"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(webhook);
          }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="sm"
          variant="soft"
          color="danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(webhook);
          }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Header with icon and name */}
      <Stack direction="row" alignItems="flex-start" gap={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 'md',
            bgcolor: 'primary.softBg',
            color: 'primary.600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <WebhookIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            level="title-md"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: 6, // Space for action buttons
            }}
          >
            {name}
          </Typography>
        </Box>
      </Stack>

      {/* URL */}
      <Tooltip title={url} placement="bottom" enterDelay={300}>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
          }}
        >
          <LinkIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography
            level="body-sm"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayUrl}
          </Typography>
        </Stack>
      </Tooltip>

      {/* Policy badge */}
      {policyName && (
        <Chip
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<TuneIcon sx={{ fontSize: 14 }} />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {policyName}
        </Chip>
      )}

      {/* Metadata row */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ mt: 'auto', pt: 1 }}
      >
        {fieldMappingsCount > 0 && (
          <Tooltip title="Field mappings">
            <Chip
              size="sm"
              variant="plain"
              color="neutral"
              startDecorator={<DataObjectIcon sx={{ fontSize: 14 }} />}
            >
              {fieldMappingsCount} field{fieldMappingsCount !== 1 ? 's' : ''}
            </Chip>
          </Tooltip>
        )}
        {headersCount > 0 && (
          <Tooltip title="Custom headers">
            <Chip
              size="sm"
              variant="plain"
              color="neutral"
            >
              {headersCount} header{headersCount !== 1 ? 's' : ''}
            </Chip>
          </Tooltip>
        )}
        {hasAuth && (
          <Tooltip title="Basic authentication enabled">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'success.600',
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}
      </Stack>

      {/* Footer with date */}
      <Typography
        level="body-xs"
        sx={{
          color: 'text.tertiary',
          mt: 0.5,
        }}
      >
        Created {formatDate(created_at)}
      </Typography>
    </MotionCard>
  );
};

export default WebhookCard;
